import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTheme } from '../context/ThemeContext';
import { designTokens } from '../styles/design-tokens';
import api from '../lib/api';

// ─── Types (mirror api-server/routes/data-status.js response shapes) ─────────

interface SourceRelease {
  id: number;
  layer: string;
  version: string;
  ingested_at: string;
  row_count: number | null;
  source_url: string | null;
  ingest_script: string | null;
  notes: string | null;
  created_by: string | null;
  ingest_duration_ms: number | null;
}

interface ProximityCoverageRow {
  layer: string;
  state: string;
  cached_rows: number;
  most_recent_compute: string;
}

interface ProximityCoverageTotal {
  state: string;
  total_parcels: number;
}

interface ProximityLayerMode {
  layer: string;
  compute_mode: 'on_demand' | 'batch';
}

interface ManualOverride {
  id: number;
  parcel_id: string | null;
  table_name: string;
  column_or_layer: string;
  override_value: unknown;
  reason: string;
  is_frozen: boolean;
  scope_identifier: string | null;
  created_at: string;
  created_by: string;
}

interface CoverageMatrixCell {
  source_layer: string;
  has_coverage: boolean | 'unknown';
  row_count: number;
  latest_version: string | null;
  ingested_at: string | null;
  notes: string | null;
}

interface CoverageMatrixRow {
  state: string;
  layers: CoverageMatrixCell[];
}

// ─── Format helpers ──────────────────────────────────────────────────────────

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(diff)) return '—';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

function ageBucket(iso: string | null): 'fresh' | 'aging' | 'stale' | 'missing' {
  if (!iso) return 'missing';
  const days = (Date.now() - new Date(iso).getTime()) / 86400000;
  if (days < 30) return 'fresh';
  if (days < 90) return 'aging';
  return 'stale';
}

function bucketColor(bucket: 'fresh' | 'aging' | 'stale' | 'missing' | 'unknown'): string {
  switch (bucket) {
    case 'fresh':   return designTokens.colors.success;
    case 'aging':   return designTokens.colors.warning;
    case 'stale':   return designTokens.colors.error;
    case 'unknown': return designTokens.colors.textTertiary;
    case 'missing':
    default:        return designTokens.colors.error;
  }
}

function formatNumber(n: number | null | undefined): string {
  if (n == null) return '—';
  return n.toLocaleString();
}

function compactJson(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'string') return v;
  try {
    const s = JSON.stringify(v);
    return s.length > 60 ? s.slice(0, 57) + '…' : s;
  } catch {
    return String(v);
  }
}

// ─── Section 1: Source Releases Timeline ─────────────────────────────────────

const SourceReleasesSection: React.FC = () => {
  const { theme } = useTheme();
  const [releases, setReleases] = useState<SourceRelease[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedLayers, setExpandedLayers] = useState<Set<string>>(new Set());

  useEffect(() => {
    setLoading(true);
    api.get('/data-status/source-releases')
      .then((res) => { setReleases(res.data.releases || []); setError(null); })
      .catch((e) => { setError(e?.response?.data?.error || e.message || 'Failed'); })
      .finally(() => setLoading(false));
  }, []);

  // Group by layer, newest-first preserved from server sort.
  const grouped = useMemo(() => {
    const m = new Map<string, SourceRelease[]>();
    for (const r of releases) {
      if (!m.has(r.layer)) m.set(r.layer, []);
      m.get(r.layer)!.push(r);
    }
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [releases]);

  const toggle = (layer: string) => {
    setExpandedLayers((prev) => {
      const next = new Set(prev);
      next.has(layer) ? next.delete(layer) : next.add(layer);
      return next;
    });
  };

  if (loading) return <SectionLoading label="source releases" />;
  if (error)   return <SectionError label="source releases" message={error} />;
  if (!releases.length) return <SectionEmpty label="No source releases logged yet." />;

  return (
    <div>
      {grouped.map(([layer, rows]) => {
        const expanded = expandedLayers.has(layer);
        const visible = expanded ? rows : rows.slice(0, 3);
        const newest = rows[0];
        return (
          <div key={layer} style={{
            marginBottom: 12,
            border: `1px solid ${theme.border}`,
            borderRadius: designTokens.borderRadius.md,
            overflow: 'hidden',
          }}>
            <div style={{
              padding: '8px 14px',
              background: theme.backgroundSecondary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <span style={{
                  display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                  background: bucketColor(ageBucket(newest.ingested_at)),
                  flexShrink: 0,
                }} />
                <span style={{
                  fontWeight: designTokens.typography.fontWeight.semibold,
                  fontSize: designTokens.typography.fontSize.md,
                  fontFamily: designTokens.typography.fontFamily.mono,
                }}>
                  {layer}
                </span>
                <span style={{ color: theme.textSecondary, fontSize: designTokens.typography.fontSize.sm }}>
                  latest: <strong>{newest.version}</strong> · {formatRelative(newest.ingested_at)}
                </span>
              </div>
              {rows.length > 3 && (
                <button onClick={() => toggle(layer)} style={ghostButton(theme)}>
                  {expanded ? `Show latest 3` : `Show all ${rows.length}`}
                </button>
              )}
            </div>
            <div style={{ padding: '4px 0' }}>
              {visible.map((r) => (
                <ReleaseRow key={r.id} release={r} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ReleaseRow: React.FC<{ release: SourceRelease }> = ({ release }) => {
  const { theme } = useTheme();
  return (
    <div style={{
      padding: '8px 14px',
      borderTop: `1px solid ${theme.border}`,
      display: 'grid',
      gridTemplateColumns: '120px 130px 90px 1fr auto',
      gap: 12,
      alignItems: 'center',
      fontSize: designTokens.typography.fontSize.sm,
    }}>
      <span style={{ fontFamily: designTokens.typography.fontFamily.mono }}>
        {release.version}
      </span>
      <span style={{ color: theme.textSecondary }}>{formatRelative(release.ingested_at)}</span>
      <span style={{ color: theme.textSecondary }}>
        {release.row_count != null ? `${formatNumber(release.row_count)} rows` : '—'}
      </span>
      <span style={{ color: theme.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {release.notes || release.ingest_script || ''}
      </span>
      <span style={{ color: theme.textTertiary, fontSize: designTokens.typography.fontSize.xs }}>
        {release.source_url
          ? <a href={release.source_url} target="_blank" rel="noopener noreferrer"
               style={{ color: designTokens.colors.brand }}>source</a>
          : (release.created_by || '')}
      </span>
    </div>
  );
};

// ─── Section 2: Coverage Matrix ──────────────────────────────────────────────

const STATE_ORDER = ['TX', 'NM', 'OK', 'AZ', 'WV'] as const;

const CoverageMatrixSection: React.FC = () => {
  const { theme } = useTheme();
  const [matrix, setMatrix] = useState<CoverageMatrixRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api.get('/data-status/coverage-matrix')
      .then((res) => { setMatrix(res.data.matrix || []); setError(null); })
      .catch((e) => { setError(e?.response?.data?.error || e.message || 'Failed'); })
      .finally(() => setLoading(false));
  }, []);

  const layerColumns = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const row of matrix) {
      for (const cell of row.layers) {
        if (!seen.has(cell.source_layer)) { seen.add(cell.source_layer); out.push(cell.source_layer); }
      }
    }
    return out;
  }, [matrix]);

  if (loading) return <SectionLoading label="coverage matrix" />;
  if (error)   return <SectionError label="coverage matrix" message={error} />;
  if (!matrix.length) return <SectionEmpty label="No coverage data available." />;

  return (
    <div style={{
      border: `1px solid ${theme.border}`,
      borderRadius: designTokens.borderRadius.md,
      overflowX: 'auto',
    }}>
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: designTokens.typography.fontSize.sm,
      }}>
        <thead>
          <tr style={{ background: theme.backgroundSecondary }}>
            <th style={headerCell(theme, { textAlign: 'left' })}>State</th>
            {layerColumns.map((layer) => (
              <th key={layer} style={headerCell(theme, {
                fontFamily: designTokens.typography.fontFamily.mono,
                textAlign: 'left',
              })}>
                {layer}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.map((row) => (
            <tr key={row.state} style={{ borderTop: `1px solid ${theme.border}` }}>
              <td style={{
                padding: '8px 12px',
                fontWeight: designTokens.typography.fontWeight.semibold,
                fontFamily: designTokens.typography.fontFamily.mono,
              }}>
                {row.state}
              </td>
              {layerColumns.map((layer) => {
                const cell = row.layers.find((l) => l.source_layer === layer);
                return <CoverageCell key={layer} cell={cell} />;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const CoverageCell: React.FC<{ cell: CoverageMatrixCell | undefined }> = ({ cell }) => {
  const { theme } = useTheme();
  if (!cell) {
    return <td style={{ padding: '8px 12px', color: theme.textTertiary }}>—</td>;
  }
  let bucket: 'fresh' | 'aging' | 'stale' | 'missing' | 'unknown';
  if (cell.has_coverage === 'unknown')        bucket = 'unknown';
  else if (cell.has_coverage === false)       bucket = 'missing';
  else                                        bucket = ageBucket(cell.ingested_at);
  const dot = bucketColor(bucket);
  const tooltip = [
    cell.latest_version ? `version ${cell.latest_version}` : null,
    cell.ingested_at ? `ingested ${formatRelative(cell.ingested_at)}` : null,
    cell.notes,
  ].filter(Boolean).join('\n');
  return (
    <td title={tooltip || undefined} style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
      <span style={{
        display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
        background: dot, marginRight: 6, verticalAlign: 'middle',
      }} />
      <span style={{ color: theme.text }}>
        {cell.has_coverage === 'unknown' ? '?' : formatNumber(cell.row_count)}
      </span>
    </td>
  );
};

// ─── Section 3: Derived Layer Coverage ───────────────────────────────────────

const ProximityCoverageSection: React.FC = () => {
  const { theme } = useTheme();
  const [layers, setLayers] = useState<ProximityLayerMode[]>([]);
  const [coverage, setCoverage] = useState<ProximityCoverageRow[]>([]);
  const [totals, setTotals] = useState<ProximityCoverageTotal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api.get('/data-status/proximity-coverage')
      .then((res) => {
        setLayers(res.data.layers || []);
        setCoverage(res.data.coverage || []);
        setTotals(res.data.total_parcels_by_state || []);
        setError(null);
      })
      .catch((e) => { setError(e?.response?.data?.error || e.message || 'Failed'); })
      .finally(() => setLoading(false));
  }, []);

  const totalsMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const t of totals) m.set(t.state, t.total_parcels);
    return m;
  }, [totals]);

  const lookup = useMemo(() => {
    const m = new Map<string, ProximityCoverageRow>();
    for (const row of coverage) m.set(`${row.layer}::${row.state}`, row);
    return m;
  }, [coverage]);

  if (loading) return <SectionLoading label="cache coverage" />;
  if (error)   return <SectionError label="cache coverage" message={error} />;

  return (
    <div>
      <p style={{
        fontSize: designTokens.typography.fontSize.xs,
        color: theme.textSecondary,
        margin: '0 0 10px 0',
        maxWidth: 760,
      }}>
        On-demand layers are computed when first accessed and cached; batch
        layers are pre-computed by scripts. Migration in progress — layers
        flip from Batch to On-demand as the wrapper enables them.
      </p>
      <div style={{
        border: `1px solid ${theme.border}`,
        borderRadius: designTokens.borderRadius.md,
        overflowX: 'auto',
      }}>
        {layers.length === 0 ? (
          <div style={{
            padding: '16px',
            color: theme.textSecondary,
            fontSize: designTokens.typography.fontSize.sm,
          }}>
            No proximity layers registered yet.
          </div>
        ) : (
          <table style={{
            width: '100%', borderCollapse: 'collapse',
            fontSize: designTokens.typography.fontSize.sm,
          }}>
            <thead>
              <tr style={{ background: theme.backgroundSecondary }}>
                <th style={headerCell(theme, { textAlign: 'left' })}>Layer</th>
                <th style={headerCell(theme, { textAlign: 'left' })}>Mode</th>
                {STATE_ORDER.map((s) => (
                  <th key={s} style={headerCell(theme, { textAlign: 'left' })}>{s}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {layers.map(({ layer, compute_mode }) => (
                <tr key={layer} style={{ borderTop: `1px solid ${theme.border}` }}>
                  <td style={{
                    padding: '8px 12px',
                    fontFamily: designTokens.typography.fontFamily.mono,
                    fontWeight: designTokens.typography.fontWeight.medium,
                  }}>
                    {layer}
                  </td>
                  <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                    <ComputeModeBadge mode={compute_mode} />
                  </td>
                  {STATE_ORDER.map((s) => {
                    const row = lookup.get(`${layer}::${s}`);
                    const cached = row?.cached_rows ?? 0;
                    const total = totalsMap.get(s) ?? 0;
                    const pct = total > 0 ? (cached / total) * 100 : 0;
                    return (
                      <td key={s} style={{ padding: '8px 12px', minWidth: 140 }}>
                        <div style={{
                          fontSize: designTokens.typography.fontSize.xs,
                          color: theme.textSecondary,
                          marginBottom: 4,
                        }}>
                          {formatNumber(cached)} / {formatNumber(total)} ({pct.toFixed(2)}%)
                        </div>
                        <div style={{
                          height: 4,
                          background: theme.border,
                          borderRadius: 2,
                          overflow: 'hidden',
                        }}>
                          <div style={{
                            width: `${Math.min(100, pct)}%`,
                            height: '100%',
                            background: pct > 50 ? designTokens.colors.success
                                      : pct > 0  ? designTokens.colors.brand
                                                 : 'transparent',
                          }} />
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const ComputeModeBadge: React.FC<{ mode: 'on_demand' | 'batch' }> = ({ mode }) => {
  const isOnDemand = mode === 'on_demand';
  const tooltip = isOnDemand
    ? 'Computed on first read, cached thereafter. Refreshes when source data version changes.'
    : 'Pre-computed by batch scripts. Stored on parcels columns directly.';
  return (
    <span
      title={tooltip}
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: designTokens.borderRadius.full,
        fontSize: designTokens.typography.fontSize.xs,
        fontWeight: designTokens.typography.fontWeight.semibold,
        color: '#fff',
        background: isOnDemand
          ? designTokens.colors.success
          : designTokens.colors.textTertiary,
        whiteSpace: 'nowrap',
      }}
    >
      {isOnDemand ? 'On-demand' : 'Batch'}
    </span>
  );
};

// ─── Section 4: Manual Overrides Audit ───────────────────────────────────────

const ManualOverridesSection: React.FC = () => {
  const { theme } = useTheme();
  const [overrides, setOverrides] = useState<ManualOverride[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api.get('/data-status/manual-overrides')
      .then((res) => { setOverrides(res.data.overrides || []); setError(null); })
      .catch((e) => { setError(e?.response?.data?.error || e.message || 'Failed'); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <SectionLoading label="manual overrides" />;
  if (error)   return <SectionError label="manual overrides" message={error} />;
  if (!overrides.length) return <SectionEmpty label="No active manual overrides." />;

  return (
    <div style={{
      border: `1px solid ${theme.border}`,
      borderRadius: designTokens.borderRadius.md,
      overflowX: 'auto',
    }}>
      <table style={{
        width: '100%', borderCollapse: 'collapse',
        fontSize: designTokens.typography.fontSize.sm,
      }}>
        <thead>
          <tr style={{ background: theme.backgroundSecondary }}>
            <th style={headerCell(theme, { textAlign: 'left' })}>Target</th>
            <th style={headerCell(theme, { textAlign: 'left' })}>Table.Column</th>
            <th style={headerCell(theme, { textAlign: 'left' })}>Override value</th>
            <th style={headerCell(theme, { textAlign: 'left' })}>Frozen</th>
            <th style={headerCell(theme, { textAlign: 'left' })}>Reason</th>
            <th style={headerCell(theme, { textAlign: 'left' })}>When · By</th>
          </tr>
        </thead>
        <tbody>
          {overrides.map((o) => (
            <tr key={o.id} style={{ borderTop: `1px solid ${theme.border}` }}>
              <td style={{
                padding: '8px 12px',
                fontFamily: designTokens.typography.fontFamily.mono,
                fontSize: designTokens.typography.fontSize.xs,
                color: theme.text,
              }}>
                {o.parcel_id ? o.parcel_id.slice(0, 8) + '…'
                  : (o.scope_identifier ? <em>{o.scope_identifier.slice(0, 10) + '…'}</em>
                                        : <em style={{ color: theme.textTertiary }}>(scope NULL)</em>)}
              </td>
              <td style={{
                padding: '8px 12px',
                fontFamily: designTokens.typography.fontFamily.mono,
                fontSize: designTokens.typography.fontSize.xs,
              }}>
                {o.table_name}.{o.column_or_layer}
              </td>
              <td style={{
                padding: '8px 12px',
                fontFamily: designTokens.typography.fontFamily.mono,
                fontSize: designTokens.typography.fontSize.xs,
                color: theme.textSecondary,
                maxWidth: 240,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }} title={JSON.stringify(o.override_value)}>
                {compactJson(o.override_value)}
              </td>
              <td style={{ padding: '8px 12px' }}>
                {o.is_frozen ? (
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: designTokens.borderRadius.full,
                    fontSize: designTokens.typography.fontSize.xs,
                    fontWeight: designTokens.typography.fontWeight.semibold,
                    background: designTokens.colors.warningLight,
                    color: designTokens.colors.warning,
                  }}>frozen</span>
                ) : (
                  <span style={{ color: theme.textTertiary }}>—</span>
                )}
              </td>
              <td style={{
                padding: '8px 12px',
                color: theme.textSecondary,
                maxWidth: 320,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }} title={o.reason}>
                {o.reason.length > 100 ? o.reason.slice(0, 97) + '…' : o.reason}
              </td>
              <td style={{
                padding: '8px 12px',
                fontSize: designTokens.typography.fontSize.xs,
                color: theme.textSecondary,
                whiteSpace: 'nowrap',
              }}>
                {formatRelative(o.created_at)} · {o.created_by}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── Shared bits ─────────────────────────────────────────────────────────────

function ghostButton(theme: ReturnType<typeof useTheme>['theme']): React.CSSProperties {
  return {
    padding: '4px 10px',
    fontSize: designTokens.typography.fontSize.xs,
    fontFamily: designTokens.typography.fontFamily.display,
    background: 'transparent',
    color: theme.text,
    border: `1px solid ${theme.border}`,
    borderRadius: designTokens.borderRadius.sm,
    cursor: 'pointer',
  };
}

function headerCell(
  theme: ReturnType<typeof useTheme>['theme'],
  extra: React.CSSProperties = {},
): React.CSSProperties {
  return {
    padding: '8px 12px',
    fontWeight: designTokens.typography.fontWeight.semibold,
    fontSize: designTokens.typography.fontSize.xs,
    color: theme.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    ...extra,
  };
}

const SectionLoading: React.FC<{ label: string }> = ({ label }) => {
  const { theme } = useTheme();
  return (
    <div style={{ color: theme.textSecondary, fontSize: designTokens.typography.fontSize.sm, padding: '8px 0' }}>
      Loading {label}…
    </div>
  );
};

const SectionError: React.FC<{ label: string; message: string }> = ({ label, message }) => (
  <div style={{
    color: designTokens.colors.error,
    fontSize: designTokens.typography.fontSize.sm,
    padding: '8px 12px',
    background: designTokens.colors.errorLight,
    borderRadius: designTokens.borderRadius.sm,
  }}>
    Failed to load {label}: {message}
  </div>
);

const SectionEmpty: React.FC<{ label: string }> = ({ label }) => {
  const { theme } = useTheme();
  return (
    <div style={{
      color: theme.textSecondary,
      fontSize: designTokens.typography.fontSize.sm,
      padding: '16px',
      border: `1px dashed ${theme.border}`,
      borderRadius: designTokens.borderRadius.md,
      textAlign: 'center',
    }}>
      {label}
    </div>
  );
};

const Legend: React.FC = () => {
  const { theme } = useTheme();
  const items: Array<{ label: string; color: string }> = [
    { label: '< 30 days',     color: designTokens.colors.success },
    { label: '30–90 days',    color: designTokens.colors.warning },
    { label: '> 90 days / none', color: designTokens.colors.error },
    { label: 'unknown',       color: designTokens.colors.textTertiary },
  ];
  return (
    <div style={{
      display: 'flex', gap: 14, flexWrap: 'wrap',
      fontSize: designTokens.typography.fontSize.xs,
      color: theme.textSecondary,
      marginTop: 6,
    }}>
      {items.map((it) => (
        <span key={it.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{
            display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
            background: it.color,
          }} />
          {it.label}
        </span>
      ))}
    </div>
  );
};

const SectionWrap: React.FC<{
  title: string;
  subtitle?: string;
  legend?: boolean;
  children: React.ReactNode;
}> = ({ title, subtitle, legend, children }) => {
  const { theme } = useTheme();
  return (
    <section style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
        <h2 style={{
          fontSize: designTokens.typography.fontSize.lg,
          fontWeight: designTokens.typography.fontWeight.semibold,
          margin: 0,
          color: theme.text,
        }}>
          {title}
        </h2>
        {subtitle && (
          <span style={{
            fontSize: designTokens.typography.fontSize.sm,
            color: theme.textSecondary,
          }}>
            {subtitle}
          </span>
        )}
      </div>
      {legend && <Legend />}
      <div style={{ marginTop: 12 }}>{children}</div>
    </section>
  );
};

// ─── Page ────────────────────────────────────────────────────────────────────

const DataStatusPage: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const { theme } = useTheme();
  const refreshKey = useRefreshKey();

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.background,
      color: theme.text,
      fontFamily: designTokens.typography.fontFamily.display,
    }}>
      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '24px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <button onClick={onBack} style={{
            padding: '4px 10px',
            fontSize: designTokens.typography.fontSize.sm,
            fontFamily: designTokens.typography.fontFamily.display,
            background: 'none',
            color: theme.textSecondary,
            border: `1px solid ${theme.border}`,
            borderRadius: designTokens.borderRadius.sm,
            cursor: 'pointer',
          }}>← Back</button>
          <h1 style={{
            fontSize: designTokens.typography.fontSize.xl,
            fontWeight: designTokens.typography.fontWeight.semibold,
            margin: 0,
          }}>Data Status</h1>
          <button onClick={refreshKey.bump} style={{
            ...ghostButton(theme),
            marginLeft: 'auto',
          }}>Refresh</button>
        </div>
        <p style={{
          fontSize: designTokens.typography.fontSize.sm,
          color: theme.textSecondary,
          margin: '0 0 24px 0',
          maxWidth: 760,
        }}>
          Source freshness, lazy-compute cache coverage, and manual override audit.
          Lives at <code style={{
            padding: '1px 6px',
            background: theme.backgroundSecondary,
            borderRadius: designTokens.borderRadius.sm,
            fontFamily: designTokens.typography.fontFamily.mono,
            fontSize: designTokens.typography.fontSize.xs,
          }}>/admin/data-status</code>; all data pulled from <code style={{
            padding: '1px 6px',
            background: theme.backgroundSecondary,
            borderRadius: designTokens.borderRadius.sm,
            fontFamily: designTokens.typography.fontFamily.mono,
            fontSize: designTokens.typography.fontSize.xs,
          }}>/api/data-status/*</code>. Read-only.
        </p>

        <div key={refreshKey.k}>
          <SectionWrap
            title="Source Releases Timeline"
            subtitle="Every ingest writes one row; newest per layer at top."
          >
            <SourceReleasesSection />
          </SectionWrap>

          <SectionWrap
            title="Coverage Matrix"
            subtitle="State × source layer. Cell tooltip shows version + ingest age + notes."
            legend
          >
            <CoverageMatrixSection />
          </SectionWrap>

          <SectionWrap
            title="Derived Layer Coverage"
            subtitle="parcel_proximity rows per (layer, state), with each layer's current compute mode."
          >
            <ProximityCoverageSection />
          </SectionWrap>

          <SectionWrap
            title="Manual Overrides Audit"
            subtitle="Active rows in manual_overrides (deleted_at IS NULL)."
          >
            <ManualOverridesSection />
          </SectionWrap>
        </div>
      </div>
    </div>
  );
};

function useRefreshKey() {
  const [k, setK] = useState(0);
  const bump = useCallback(() => setK((x) => x + 1), []);
  return { k, bump };
}

export default DataStatusPage;
